/**
 * Componente para mostrar la lista de archivos de participantes.
 *
 * Este componente renderiza una tabla con todos los archivos de carga masiva
 * de participantes, incluyendo información del estado, estadísticas de procesamiento
 * y acciones para ver reportes y descargar archivos.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-12]
 * @updated [Fecha de última modificación: 2025-08-12]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import PropTypes from "prop-types";
import { Pagination, Stack, Chip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import usePermission from "hooks/usePermission";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useNavigate, useParams } from "react-router-dom";

export default function ParticipantFilesList({
  participantFilesList = [],
  entriesStart,
  entriesEnd,
  totalData,
  totalPages,
  currentPage,
  handlePageChange,
  handleViewReport,
  handleDownloadFile,
  callHandleClickSubmit,
  activityName,
}) {
  const navigate = useNavigate();
  const { id: activityId } = useParams();
  const canCreate = usePermission("Crear", "Participantes");

  const canListParticipant = usePermission("Listar", "Participantes");

  /**
   * Formatea una fecha para mostrarla en formato legible
   * @param {string} dateString - Fecha en formato string
   * @returns {string} Fecha formateada
   */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "Fecha inválida"
      : date.toISOString().slice(0, 19).replace("T", " ");
  };

  /**
   * Obtiene el color del chip según el estado
   * @param {string} status - Estado del procesamiento
   * @returns {string} Color del chip
   */
  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
      case "Completado":
        return "success";
      case "FAILED":
      case "Fallido":
        return "error";
      case "PARTIAL":
      case "Parcialmente exitoso":
        return "warning";
      case "PROCESSING":
      case "Procesando":
        return "info";
      default:
        return "default";
    }
  };

  /**
   * Obtiene el texto del estado en español
   * @param {string} status - Estado del procesamiento
   * @returns {string} Texto del estado
   */
  const getStatusText = (status) => {
    switch (status) {
      case "COMPLETED":
        return "Completado";
      case "FAILED":
        return "Fallido";
      case "PARTIAL":
        return "Parcial";
      case "PROCESSING":
        return "Procesando";
      default:
        return status;
    }
  };

  const columns = [
    { Header: "Nombre del archivo", accessor: "filename", align: "left" },
    { Header: "Estado", accessor: "status", align: "center" },
    { Header: "Total filas", accessor: "total_rows", align: "center" },
    { Header: "Exitosas", accessor: "successful_rows", align: "center" },
    { Header: "Errores", accessor: "error_rows", align: "center" },
    { Header: "Fecha carga", accessor: "created", align: "center" },
    { Header: "Fecha procesamiento", accessor: "processed_at", align: "center" },
    { Header: "Responsable", accessor: "email", align: "center" },
    { Header: "Acciones", accessor: "action", width: "15%", align: "center", disableSortBy: true },
  ];

  const rows = participantFilesList.map((file) => ({
    filename: (
      <MDTypography variant="body2" style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}>
        {file.original_filename}
      </MDTypography>
    ),
    status: (
      <Chip
        label={getStatusText(file.status_display)}
        color={getStatusColor(file.status)}
        size="small"
      />
    ),
    total_rows: (
      <MDTypography variant="body2" style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}>
        {file.total_rows}
      </MDTypography>
    ),
    successful_rows: (
      <MDTypography
        variant="body2"
        style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif", color: "green" }}>
        {file.successful_rows}
      </MDTypography>
    ),
    error_rows: (
      <MDTypography
        variant="body2"
        style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif", color: "red" }}>
        {file.error_rows}
      </MDTypography>
    ),
    created: (
      <MDTypography variant="body2" style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}>
        {formatDate(file.created)}
      </MDTypography>
    ),
    processed_at: (
      <MDTypography variant="body2" style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}>
        {formatDate(file.processed_at)}
      </MDTypography>
    ),
    email: (
      <MDTypography variant="body2" style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}>
        {file.uploaded_by.email || "N/A"}
      </MDTypography>
    ),
    action: (
      <MDBox style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        {canListParticipant && (
          <a
            data-id={file}
            onClick={() =>
              navigate(`/Actividades-P/${activityId}/${file.id}`, {
                state: { id: activityId, id_file: file.id, activityTitle: activityName },
              })
            }
            title="Ver Reporte"
            style={{
              cursor: "pointer",
              textDecoration: "none",
              color: "inherit",
              marginLeft: "8px",
            }}>
            <AssessmentIcon
              style={{
                fontSize: "200px",
                width: "24px",
                height: "24px",
                backgroundImage: "#000000",
              }}
            />
          </a>
        )}
        {canCreate && (
          <a
            data-id={file.id}
            onClick={() => handleDownloadFile(file.id)}
            title="Descargar archivo"
            style={{
              cursor: "pointer",
              textDecoration: "none",
              color: "inherit",
              marginLeft: "8px",
            }}>
            <DownloadIcon
              style={{
                fontSize: "200px",
                width: "24px",
                height: "24px",
                backgroundImage: "#000000",
              }}
            />
          </a>
        )}
      </MDBox>
    ),
  }));

  return (
    <>
      {canListParticipant && (
        <>
          <DataTable
            table={{ columns, rows }}
            isSorted={true}
            entriesPerPage={false}
            showTotalEntries={false}
            canSearch={false}
            noEndBorder={false}
          />

          {!participantFilesList || participantFilesList.length === 0 ? (
            <MDBox style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <MDTypography
                variant="button"
                color="secondary"
                fontWeight="regular"
                style={{ fontSize: "20px", fontFamily: "Roboto, sans-serif" }}>
                No hay archivos de carga masiva
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <MDBox style={{ marginLeft: "20px" }} mb={{ xs: 3, sm: 0 }}>
                <MDTypography
                  style={{ left: "20px" }}
                  variant="button"
                  color="secondary"
                  fontWeight="regular">
                  Mostrando {entriesStart} hasta {entriesEnd} de {totalData} resultados en total
                </MDTypography>
              </MDBox>
              <Stack style={{ marginLeft: "20px", marginBottom: "10px" }} spacing={2}>
                <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
              </Stack>
            </>
          )}
        </>
      )}
    </>
  );
}

ParticipantFilesList.propTypes = {
  participantFilesList: PropTypes.array,
  entriesStart: PropTypes.number,
  entriesEnd: PropTypes.number,
  totalData: PropTypes.number,
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  handlePageChange: PropTypes.func,
  handleViewReport: PropTypes.func.isRequired,
  handleDownloadFile: PropTypes.func.isRequired,
  callHandleClickSubmit: PropTypes.func.isRequired,
  activityName: PropTypes.string,
};
